// Performance monitoring service for the application

interface PerformanceMetric {
  count: number;
  total: number;
  min: number;
  max: number;
  avg: number;
  lastValue: number;
  recentValues: number[];
  timestamps: number[];
}

/**
 * Interface for qualitative metrics (string labels) 
 * Used for categorizing and segmenting performance data
 */
interface LabelMetric {
  labels: string[];
  counts: Record<string, number>;
  lastValue: string;
  timestamp: number;
}

interface PerformanceStats {
  count: number;
  total: number;
  min: number;
  max: number;
  avg: number;
  last: number;
  recentCount?: number;
  recentAvg?: number;
}

interface SlowOperation {
  component: string;
  operation: string;
  duration: number;
  timestamp: number;
}

export class PerformanceService {
  private metrics: Record<string, Record<string, PerformanceMetric>> = {};
  private labelMetrics: Record<string, Record<string, LabelMetric>> = {};
  private slowOperations: SlowOperation[] = [];
  private readonly MAX_RECENT_VALUES = 10;
  private readonly SLOW_THRESHOLD_MS = 500;
  private isInitialized = false;

  constructor() {
    this.initialize();
  }

  private initialize() {
    // Initialize metrics storage
    if (!this.metrics) {
      this.metrics = {};
    }
    if (!this.labelMetrics) {
      this.labelMetrics = {};
    }
    this.isInitialized = true;
  }

  // Record when a component view loads
  public recordViewLoad(component: string, instanceId: string) {
    if (!this.isInitialized) {
      this.initialize();
    }

    // Record the timestamp when this view was loaded
    this.recordStartTime(component, instanceId, 'load');
  }

  // Record when a component view unloads
  public recordViewUnload(component: string, instanceId: string) {
    if (!this.isInitialized) {
      this.initialize();
    }

    const duration = this.recordEndTime(component, instanceId, 'load');
    if (duration > this.SLOW_THRESHOLD_MS) {
      this.recordSlowOperation(component, 'view-lifetime', duration);
    }
  }

  // Record the start time of an operation
  private recordStartTime(component: string, instanceId: string, operation: string) {
    const key = `${instanceId}-${operation}-start`;
    performance.mark(key);
  }

  // Record the end time of an operation and calculate the duration
  private recordEndTime(component: string, instanceId: string, operation: string): number {
    const startKey = `${instanceId}-${operation}-start`;
    const endKey = `${instanceId}-${operation}-end`;

    try {
      performance.mark(endKey);
      const measure = performance.measure(`${instanceId}-${operation}`, startKey, endKey);
      const duration = measure.duration;

      this.recordMetric(component, operation, duration);
      return duration;
    } catch (error) {
      
      return 0;
    }
  }

  // Record a specific operation metric
  public recordOperationMetric(component: string, instanceId: string, operation: string, duration: number) {
    if (!this.isInitialized) {
      this.initialize();
    }

    const operationKey = `${operation}`;
    this.recordMetric(component, operationKey, duration);

    if (duration > this.SLOW_THRESHOLD_MS) {
      this.recordSlowOperation(component, operation, duration);
    }
  }

  // Record a metric value
  /**
   * Record a numeric metric value
   * @param component - The component or module being measured
   * @param metric - The name of the metric
   * @param value - The numeric value to record
   */
  public recordMetric(component: string, metric: string, value: number) {
    if (!this.isInitialized) {
      this.initialize();
    }

    // Initialize component metrics object if it doesn't exist
    if (!this.metrics[component]) {
      this.metrics[component] = {};
    }

    // Initialize this specific metric if it doesn't exist
    if (!this.metrics[component][metric]) {
      this.metrics[component][metric] = {
        count: 0,
        total: 0,
        min: Number.MAX_VALUE,
        max: 0,
        avg: 0,
        lastValue: 0,
        recentValues: [],
        timestamps: []
      };
    }

    const metricObj = this.metrics[component][metric];

    // Update the metric
    metricObj.count++;
    metricObj.total += value;
    metricObj.min = Math.min(metricObj.min, value);
    metricObj.max = Math.max(metricObj.max, value);
    metricObj.avg = metricObj.total / metricObj.count;
    metricObj.lastValue = value;

    // Track recent values for trending
    metricObj.recentValues.push(value);
    metricObj.timestamps.push(Date.now());

    // Keep only the most recent values
    if (metricObj.recentValues.length > this.MAX_RECENT_VALUES) {
      metricObj.recentValues.shift();
      metricObj.timestamps.shift();
    }
  }

  // Record a slow operation for further analysis
  private recordSlowOperation(component: string, operation: string, duration: number) {
    this.slowOperations.push({
      component,
      operation,
      duration,
      timestamp: Date.now()
    });

    // Keep only the most recent slow operations
    if (this.slowOperations.length > 100) {
      this.slowOperations.shift();
    }
  }

  // Get metrics for a specific component
  public getComponentMetrics(component: string): Record<string, PerformanceStats> | null {
    if (!this.metrics[component]) {
      return null;
    }

    const result: Record<string, PerformanceStats> = {};

    for (const [metric, data] of Object.entries(this.metrics[component])) {
      result[metric] = this.calculateStats(data);
    }

    return result;
  }

  // Get metrics for all components
  public getAllMetrics(): Record<string, Record<string, PerformanceStats>> {
    const result: Record<string, Record<string, PerformanceStats>> = {};

    for (const [component, metrics] of Object.entries(this.metrics)) {
      result[component] = {};

      for (const [metric, data] of Object.entries(metrics)) {
        result[component][metric] = this.calculateStats(data);
      }
    }

    return result;
  }

  // Get slow operations for analysis
  public getSlowOperations(): SlowOperation[] {
    return [...this.slowOperations];
  }

  // Calculate stats for a metric
  private calculateStats(metric: PerformanceMetric): PerformanceStats {
    const stats: PerformanceStats = {
      count: metric.count,
      total: metric.total,
      min: metric.min,
      max: metric.max,
      avg: metric.avg,
      last: metric.lastValue
    };

    // Calculate recent stats if we have enough data
    const recentDurations = [...metric.recentValues];
    if (recentDurations.length > 0) {
      stats.recentCount = recentDurations.length;
      stats.recentAvg = recentDurations.reduce((sum, val) => sum + val, 0) / recentDurations.length;
    }

    return stats;
  }
  
  /**
   * Record a string label metric for categorization purposes
   * 
   * This method handles qualitative or categorical metrics like:
   * - View modes ("monthly", "weekly", "daily")
   * - User types ("free", "plus")
   * - Feature usage ("enabled", "disabled")
   * 
   * @param component - The component or module being measured
   * @param metric - The name of the categorical metric
   * @param label - The string label value to record
   */
  public recordLabelMetric(component: string, metric: string, label: string) {
    if (!this.isInitialized) {
      this.initialize();
    }
    
    // Initialize component label metrics object if it doesn't exist
    if (!this.labelMetrics[component]) {
      this.labelMetrics[component] = {};
    }
    
    // Initialize metric entry if it doesn't exist
    if (!this.labelMetrics[component][metric]) {
      this.labelMetrics[component][metric] = {
        labels: [],
        counts: {},
        lastValue: label,
        timestamp: Date.now()
      };
    }
    
    const metricData = this.labelMetrics[component][metric];
    
    // Add the label if not seen before
    if (!metricData.labels.includes(label)) {
      metricData.labels.push(label);
    }
    
    // Increment the count for this label
    metricData.counts[label] = (metricData.counts[label] || 0) + 1;
    
    // Update the last value and timestamp
    metricData.lastValue = label;
    metricData.timestamp = Date.now();
  }
  
  /**
   * Get stats for a label metric (frequencies, etc.)
   * 
   * @param component - The component or module name
   * @param metric - The metric name
   * @returns Object containing label stats or null if no data
   */
  public getLabelMetricStats(component: string, metric: string) {
    if (!this.labelMetrics[component] || !this.labelMetrics[component][metric]) {
      return null;
    }
    
    const metricData = this.labelMetrics[component][metric];
    const totalCount = Object.values(metricData.counts).reduce((sum, count) => sum + count, 0);
    
    // Calculate frequency for each label
    const frequencies: Record<string, number> = {};
    for (const label of metricData.labels) {
      // Use nullish coalescing to handle potentially undefined counts
      const count = metricData.counts[label] ?? 0;
      frequencies[label] = count / totalCount;
    }
    
    return {
      labels: metricData.labels,
      counts: metricData.counts,
      totalCount,
      frequencies,
      lastValue: metricData.lastValue,
      timestamp: metricData.timestamp
    };
  }

  // Clear all metrics
  public clearMetrics() {
    this.metrics = {};
    this.slowOperations = [];
  }
}

export const performanceService = new PerformanceService();